/**
 * Текущий пользователь (GET /api/v1/auth/me/) под общим ключом ['currentUser'],
 * тем же, что у меню и AdminGuard. KAN-735: признак владельца для плашки расходов.
 * KAN-811: роль MANAGER (вход менеджера) и его запись справочника.
 * KAN-863: признак номера кабинета для звонков.
 * KAN-866: имя, email и организация для шапки кабинета.
 * KAN-925: профиль аккаунта из ответа дописывает перехватчик apiClient (для
 * любого queryFn этого ключа), здесь только запрос.
 */
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import { isManagerUser, type ApiAccessRule } from '@/lib/managerAccess';
import type { PersonalTelegramBrief } from '@/services/api/personalTelegram';

export type CurrentUserRole = 'OWNER' | 'USER' | 'MANAGER';

export interface CurrentUser {
  id?: string;
  /** KAN-866: кто вошёл и чей кабинет (шапка CabinetIdentity). */
  email?: string;
  first_name?: string;
  last_name?: string;
  organization?: { id: string; name: string } | null;
  role?: CurrentUserRole | string;
  /** KAN-811: запись справочника менеджеров (только у роли MANAGER). */
  manager_id?: string | null;
  manager_name?: string | null;
  /**
   * KAN-909: запись справочника, связанная со входом любой роли (у владельца
   * Стомбая "Стомбэй"); умолчание фильтров по менеджеру. Нет поля - только manager_id.
   */
  linked_manager_id?: string | null;
  /** KAN-828: менеджер старший (переназначает любых клиентов); у не-менеджеров false/нет. */
  is_senior_manager?: boolean;
  /** KAN-811: правила доступа менеджера к API от backend; у остальных null. */
  api_access?: ApiAccessRule[] | null;
  /** KAN-840: часовой пояс организации (IANA), по умолчанию Europe/Moscow. */
  organization_timezone?: string | null;
  /**
   * KAN-863: в кабинете подключён номер для звонков через платформу. false - кнопка
   * "Позвонить" выключена; нет поля (старый backend) - поведение прежнее.
   */
  cabinet_has_call_number?: boolean;
  /** KAN-871: свой личный Telegram-аккаунт менеджера; null - не подключён или не менеджер. */
  personal_telegram_account?: PersonalTelegramBrief | null;
  /** KAN-988: функции тарифа кабинета (коды lib/features.ts); нет поля - функций нет. */
  features?: string[];
  /** KAN-980: суперпользователь платформы (раздел /platform); нет поля - нет доступа. */
  is_superuser?: boolean;
}

async function fetchCurrentUser(): Promise<CurrentUser> {
  const res = await apiClient.get('/api/v1/auth/me/');
  return res.data;
}

export function useCurrentUser() {
  return useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * KAN-871: текущий пользователь только из кеша ['currentUser'] (его грузят
 * раскладка и ManagerRouteGuard), без своего запроса; обновляется вместе с
 * кешем (после подключения личного Telegram блок профиля сбрасывает ключ).
 */
export function useCachedCurrentUser(): CurrentUser | undefined {
  return useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000,
    enabled: false,
  }).data;
}

/** Владелец организации (как пункты меню ownerOnly). */
export function useIsOwner(): boolean {
  const { data } = useCurrentUser();
  return data?.role === 'OWNER';
}

/** KAN-811: вошёл менеджер (роль MANAGER). */
export function useIsManager(): boolean {
  const { data } = useCurrentUser();
  return isManagerUser(data);
}
